import React, { useState, useRef } from 'react';
import { useHistory } from "react-router-dom";
import {
    Box,
    Button,
    Typography,
    makeStyles,
    MenuItem,
    Select,
    Container,
    FormControl,
    Stepper,
    Step,
    StepLabel,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Tabs,
    Tab
} from '@material-ui/core';
import PublishIcon from '@material-ui/icons/Publish';
import CloseIcon from '@material-ui/icons/Close';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import UploadDetailsSavedDialog from '../../ModalPopups/UploadDetailsSavedDialog';
import axios from 'axios';
import { useApiBaseUrl } from '../../../../common/CommonMethods/CommonMethods';
import { appUrl } from '../../../../../../../utils/config';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';


const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
    },
    stepperHeader: {
        padding: "32px 0",
        textAlign: "center",
        backgroundColor: "#F5F9FF",
        position: "relative",
    },
    heading: {
        fontWeight: 600,
        fontSize: 38,
        lineHeight: "100%",
        marginBottom: 20,
    },
    subheading: {
        fontWeight: 400,
        fontSize: 24,
        lineHeight: "100%",
    },
    closePageAction: {
        position: "absolute",
        top: 20,
        right: 20,
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        fontWeight: "bold",
        color: "#000",
        "& svg": {
            marginLeft: 4,
            color: "red",
        },
    },
    uploadBox: {
        width: "100%",
        marginTop: "2em",
        border: "1px solid black",
        backgroundColor: "#f5f5f5",
        padding: "2em 0",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "pointer",
        minHeight: 60,
    },
    uploadText: {
        marginLeft: "auto",
        textAlign: "center",
        flex: 1,
        fontSize: 16,
    },
    uploadIcon: {
        fontSize: "40px",
        marginLeft: "auto",
        marginRight: "20px",
        color: "#333",
    },
    file: {
        marginTop: "2em",
        display: "flex",
        flexDirection: "column",
        gap: "1em",
        alignItems: "flex-start",
    },
    link: {
        color: "blue",
        textDecoration: "underline",
    },
    linkBox: {
        cursor: "pointer",
        display: "flex",
        gap: "0.5em",
    },
    bold: {
        fontWeight: 500,
    },
    criteria: {
        maxWidth: "500px",
    },
    uploadBtn: {
        backgroundColor: "#ec6535",
        color: "white",
        textTransform: "none",
        borderRadius: "50px",
        margin: "1em auto",
        padding: "5px 30px",
        "&:hover": {
            backgroundColor: "#ec6535",
        },
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: "#ec6535",
    },
    filePreview: {
        marginLeft: theme.spacing(4),
        marginBottom: theme.spacing(2),
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 8,
    },
    fileName: {
        color: "#2592f9",
        borderBottom: "2px solid #2592f9",
        paddingBottom: 2,
        cursor: "pointer",
    },
    fileSelect: {
        marginLeft: theme.spacing(4),
        marginBottom: theme.spacing(2),
        fontWeight: "bold"
    },
    uploadButton: {
        display: "flex",
        justifyContent: "center",
        margin: theme.spacing(4, 0),
    },
    uploadButtonStyle: {
        backgroundColor: '#ec6535',
        // width: "170px", // Removed fixed width
        color: '#fff',
        borderRadius: 20,
        padding: '6px 24px',
        textTransform: 'none',
        display: 'flex',
        alignItems: 'center',
        fontSize: "16px",
        gap: 8,
        whiteSpace: 'nowrap',
        '&:hover': {
            backgroundColor: '#c6532b',
        },
    },
    validationSummary: {
        padding: "2em",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
        margin: "2em 0",
    },
    summaryItem: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "1em",
        margin: "0.5em 0",
        borderRadius: "4px",
        backgroundColor: "white",
    },
    validCount: {
        color: "#4caf50",
        fontWeight: "bold",
    },
    invalidCount: {
        color: "#f44336",
        fontWeight: "bold",
    },
    totalCount: {
        color: "#2196f3",
        fontWeight: "bold",
    },
    container: {
        textAlign: "center",
        padding: "1rem"
    },
    itemGrid: {
        border: "2px solid #707070",
        padding: "1rem",
    },
    number: {
        fontSize: "2.4rem",
        fontWeight: 500
    },
    totalRecordsBox: {
        border: "2px solid #707070",
        padding: "1rem",
    },
    validRecordsBox: {
        border: "2px solid #707070",
        padding: "1rem",
    },
    invalidRecordsBox: {
        border: "2px solid #707070",
        padding: "1rem",
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginTop: theme.spacing(3),
        width: "100%",
    },
    reUploadButton: {
        marginRight: "auto",
        alignSelf: "flex-start",
        textDecoration: "underline",
        cursor: "pointer",
        textTransform: "none",
    },
    sectionAccordion: {
        marginBottom: 8,
        textAlign: "left",
    },
    sectionLabel: {
        fontWeight: 600,
    },
    sectionErrorText: {
        color: "#c62828",
    },
    sectionSuccessText: {
        color: "#4caf50",
    },
}));

const getSteps = () => ["Upload file", "Validation summary"];

// The four master-data sections returned by /uploadMasterData and /submitMasterData.
const SECTIONS = [
    { key: "product", label: "Product" },
    { key: "project", label: "Project" },
    { key: "campaign", label: "Campaign" },
    { key: "material", label: "Material" },
];

// Safely derive the valid / rejected / total counts for a single upload section.
const getSectionCounts = (node) => {
    const valid = node?.validRecordsCount ?? node?.validRecords?.length ?? 0;
    const invalid = node?.invalidRecordsCount ?? node?.invalidRecords?.length ?? 0;
    return { valid, invalid, total: valid + invalid };
};

// Normalise a single section of the /submitMasterData response into a shape the
// UI can render uniformly. `product` reports added/updated + add/update results;
// project/campaign/material report a flat `results` array. A section is only "ok"
// when it did not fail at the section level and has no failed rows.
const getSectionResult = (key, node) => {
    if (!node) {
        return { present: false, ok: true, message: "", added: 0, updated: 0, successCount: 0, failures: [] };
    }

    const sectionOk = node.status !== false;
    let added = 0;
    let updated = 0;
    let successCount = 0;
    let failures = [];

    if (key === "product") {
        added = node.added || 0;
        updated = node.updated || 0;
        const rows = [...(node.addResults || []), ...(node.updateResults || [])];
        successCount = rows.filter((r) => r && r.status === true).length;
        failures = rows
            .filter((r) => r && r.status === false)
            .map((r) => ({
                id: r.productCode || r.collabrixProductID || "",
                error: r.errorMessage || r.errorDescription || r.message || "Failed",
            }));
    } else {
        const rows = node.results || [];
        successCount = rows.filter((r) => r && r.status === true).length;
        failures = rows
            .filter((r) => r && r.status === false)
            .map((r) => ({
                id:
                    r.bmsProductCode ||
                    r.bmsProjectID ||
                    r.bmsProjectId ||
                    r.saiCampaignID ||
                    r.saiMaterialCode ||
                    (r.data && (r.data.collabrixProjectID || r.data.collabrixCampaignID || r.data.collabrixBomID)) ||
                    "",
                error: r.errorMessage || r.errorDescription || r.message || "Failed",
            }));
    }

    return {
        present: true,
        ok: sectionOk && failures.length === 0,
        message: node.message || "",
        added,
        updated,
        successCount,
        failures,
    };
};

// True only when every section that came back succeeded fully (section-level
// status and every row). A top-level status:true is NOT sufficient on its own.
const allSectionsSucceeded = (data) =>
    SECTIONS.every(({ key }) => getSectionResult(key, data?.[key]).ok);

const UploadProductDetails = () => {
    const history = useHistory();
    const classes = useStyles();
    const [file, setFile] = useState(null);
    const [fileType, setFileType] = useState('xlsx');
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [hasUploadedSuccessfully, setHasUploadedSuccessfully] = useState(false);
    const [uploadErrors, setUploadErrors] = useState([]);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [validationData, setValidationData] = useState(null);
    const [submitResults, setSubmitResults] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedSection, setSelectedSection] = useState("product");

    const fileInputRef = useRef();
    const API_BASE_URL = useApiBaseUrl(appUrl);
    const steps = getSteps();

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile && selectedFile.name.endsWith('.xlsx')) {
            setFile(selectedFile);
            setUploadErrors([]);
            setError(null);
            setHasUploadedSuccessfully(false);
        } else {
            alert('Please upload a .xlsx file');
        }
        e.target.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile && droppedFile.name.endsWith('.xlsx')) {
            setFile(droppedFile);
            setUploadErrors([]);
            setError(null);
            setHasUploadedSuccessfully(false);
        } else {
            alert('Please upload a .xlsx file');
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleRemoveFile = () => {
        if (isUploading) return;
        setFile(null);
        setUploadErrors([]);
        setError(null);
        setHasUploadedSuccessfully(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleUploadBoxClick = () => {
        if (!file || uploadErrors.length > 0 || error) {
            fileInputRef.current.click();
        }
    };

    const getAcceptAttribute = (type) => {
        return ".xlsx, .xls";
    };

    const handleNext = () => setActiveStep((prev) => prev + 1);

    // Return to the upload step, discarding any previous submit results.
    const handleReupload = () => {
        setSubmitResults(null);
        setHasUploadedSuccessfully(false);
        setActiveStep(0);
    };

    const handleUpload = async () => {
        if (file && !isUploading) {
            setIsUploading(true);
            setUploadErrors([]);
            setError(null);
            try {
                const formData = new FormData();
                formData.append('files', file);

                const response = await axios.post(`${API_BASE_URL}/uploadMasterData`, formData);
                const data = response.data;

                setIsUploading(false);

                // A validation response always carries the four section objects, even when
                // status is false because some rows failed validation. Only treat the
                // response as a hard failure (unreadable file / wrong format) when no
                // section data is present.
                const hasSectionData = SECTIONS.some(({ key }) => data && data[key]);

                if (data.status === false && !hasSectionData && (data.errorDescription || data.message)) {
                    // Hard failure (e.g. unreadable file / wrong format) — stay on the upload step.
                    setUploadErrors([data.errorDescription || data.message].filter(Boolean));
                    setHasUploadedSuccessfully(false);
                    setUploadSuccess(false);
                } else {
                    // Store the full 4-section response and move to the validation summary.
                    setUploadErrors([]);
                    setHasUploadedSuccessfully(true);
                    setValidationData(data);
                    setSubmitResults(null);
                    handleNext();
                }
            } catch (error) {
                setIsUploading(false);
                if (error.response && error.response.data && (error.response.data.errorDescription || error.response.data.message)) {
                    setUploadErrors([
                        error.response.data.errorDescription || error.response.data.message
                    ].filter(Boolean));
                } else {
                    setUploadErrors(['An error occurred while uploading. Please try again.']);
                }
            }
        }
    };

    // Total valid records across all four sections — used to gate the Submit button.
    const totalValidCount = SECTIONS.reduce(
        (sum, { key }) => sum + getSectionCounts(validationData?.[key]).valid,
        0
    );

    // Added / updated totals across the submit response — drives the success
    // dialog wording. Only the product section reports add vs update; other
    // sections contribute 0 and the dialog falls back to "saved".
    const saveOutcome = SECTIONS.reduce(
        (acc, { key }) => {
            const r = getSectionResult(key, submitResults?.[key]);
            return { added: acc.added + (r.added || 0), updated: acc.updated + (r.updated || 0) };
        },
        { added: 0, updated: 0 }
    );

    const handleSubmitValidRecords = async () => {
        if (isSubmitting) return;

        // Each array is the matching section's validRecords, sent back unmodified.
        const requestBody = {
            products: validationData?.product?.validRecords || [],
            projects: validationData?.project?.validRecords || [],
            campaigns: validationData?.campaign?.validRecords || [],
            materials: validationData?.material?.validRecords || [],
        };

        const hasAnyRecord =
            requestBody.products.length ||
            requestBody.projects.length ||
            requestBody.campaigns.length ||
            requestBody.materials.length;
        if (!hasAnyRecord) return;

        setIsSubmitting(true);
        setError(null);
        try {
            const response = await axios.post(`${API_BASE_URL}/submitMasterData`, requestBody);
            const data = response.data;

            // Always surface the per-section outcome; a top-level status:true does not
            // mean every section succeeded, so check each section individually.
            setSubmitResults(data);
            if (allSectionsSucceeded(data)) {
                setUploadSuccess(true);
                setHasUploadedSuccessfully(true);
            }
        } catch (error) {
            const errMsg = error?.response?.data?.errorDescription ||
                error?.response?.data?.message ||
                "An error occurred during submission.";
            setError(errMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCloseDialog = () => {
        setUploadSuccess(false);
        setFile(null);
        setFileType('xlsx');
        setIsUploading(false);
        setHasUploadedSuccessfully(false);
        setUploadErrors([]);
        setError(null);
        setActiveStep(0);
        setValidationData(null);
        setSubmitResults(null);
        setIsSubmitting(false);
        history.push(`/pcmp/${localStorage.collabType}/${localStorage.roleName}/${localStorage.organizationName}/${localStorage.ecosystemId}/products`)
    };

    const handleGoBack = () => {
        history.goBack()
    };

    const displayError = (
        <Container
            style={{
                width: "100%",
                border: "2px solid red",
                backgroundColor: "#ffeaea",
                padding: "0.8rem",
                marginBottom: "1.5em",
                textAlign: "center",
                fontSize: "0.875rem",
            }}
        >
            <Typography variant="body2">{error}</Typography>
        </Container>
    );

    // Step 1 sub-view: per-section outcome of the /submitMasterData call.
    const renderSubmitResults = () => {
        const allOk = allSectionsSucceeded(submitResults);
        return (
            <Container className={classes.container}>
                <Typography variant="h5" style={{ marginBottom: "1em" }}>
                    Submission Summary
                </Typography>

                {submitResults?.message && (
                    <Typography variant="body2" style={{ marginBottom: "1em" }}>
                        {submitResults.message}
                    </Typography>
                )}

                {SECTIONS.map(({ key, label }) => {
                    const node = submitResults?.[key];
                    if (!node) return null; // nothing was submitted for this section
                    const result = getSectionResult(key, node);
                    return (
                        <Accordion
                            key={key}
                            defaultExpanded={!result.ok}
                            className={classes.sectionAccordion}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={3}>
                                        <Typography className={classes.sectionLabel}>{label}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Chip
                                            size="small"
                                            label={result.ok ? "Success" : "Attention needed"}
                                            style={{
                                                backgroundColor: result.ok ? "#e6f4ea" : "#fdecea",
                                                color: result.ok ? "#1e7e34" : "#c62828",
                                                fontWeight: 600,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2">
                                            {key === "product"
                                                ? `Added: ${result.added}   Updated: ${result.updated}   Failed: ${result.failures.length}`
                                                : `Succeeded: ${result.successCount}   Failed: ${result.failures.length}`}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </AccordionSummary>
                            <AccordionDetails
                                style={{ display: "flex", flexDirection: "column", textAlign: "left" }}
                            >
                                {node.message && (
                                    <Typography variant="body2" gutterBottom>{node.message}</Typography>
                                )}
                                {result.failures.length > 0 ? (
                                    result.failures.map((failure, index) => (
                                        <Typography
                                            key={index}
                                            variant="body2"
                                            gutterBottom
                                            display="block"
                                            className={classes.sectionErrorText}
                                        >
                                            {failure.id ? `${failure.id} - ${failure.error}` : failure.error}
                                        </Typography>
                                    ))
                                ) : (
                                    <Typography variant="body2" className={classes.sectionSuccessText}>
                                        All records processed successfully.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}

                <Box className={classes.buttonContainer}>
                    {!allOk && (
                        <Button className={classes.reUploadButton} onClick={handleReupload}>
                            &lt; Re-upload File
                        </Button>
                    )}
                    <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            className={classes.uploadButtonStyle}
                            onClick={handleCloseDialog}
                        >
                            Done
                            <ChevronRightIcon />
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    };

    // Step 1 sub-view: per-section validation summary from the /uploadMasterData call.
    // The section is chosen from a dropdown; the stat boxes and rejected-record
    // list below reflect the selected section.
    const renderValidationSummary = () => {
        const node = validationData?.[selectedSection];
        const { valid, invalid, total } = getSectionCounts(node);
        const invalids = node?.invalidRecords || [];

        return (
            <Container className={classes.container}>
                {error && displayError}
                <Typography variant="h5" style={{ marginBottom: "1em" }}>
                    Validation Summary
                </Typography>

                {/* Section selector — all four sections shown as tabs */}
                <Box style={{ marginBottom: "1.5em" }}>
                    <Tabs
                        value={selectedSection}
                        onChange={(e, value) => setSelectedSection(value)}
                        indicatorColor="secondary"
                        textColor="secondary"
                        centered
                        style={{ color: "black" }}
                    >
                        {SECTIONS.map(({ key, label }) => (
                            <Tab
                                key={key}
                                value={key}
                                style={{ color: selectedSection === key ? "#1890ff" : "black" }}
                                label={label}
                            />
                        ))}
                    </Tabs>
                </Box>

                {/* Summary of Records */}
                <Grid container spacing={3} justify="space-between">
                    {[
                        { label: "Total Records", value: total, className: classes.totalRecordsBox },
                        { label: "Valid Records", value: valid, className: classes.validRecordsBox },
                        { label: "Rejected Records", value: invalid, className: classes.invalidRecordsBox },
                    ].map(({ label, value, className }, index) => (
                        <Grid item xs={12} sm={3} key={index}>
                            <Box className={className}>
                                <Typography>{label}</Typography>
                                <Typography className={classes.number}>{value}</Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Rejected Records */}
                {invalids.length > 0 && (
                    <Grid item xs={12}>
                        <Box width="100%" style={{ margin: "1em 0" }}>
                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle2">Rejected record(s) error(s)</Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "flexStart",
                                        textAlign: "left",
                                    }}
                                >
                                    {invalids.map((records, recordIndex) => (
                                        <React.Fragment key={recordIndex}>
                                            {(records.errors || []).map((err, errorIndex) => (
                                                <Typography
                                                    key={`${recordIndex}-${errorIndex}`}
                                                    variant="body1"
                                                    gutterBottom
                                                    display="block"
                                                >{`Row ${records?.row} - ${err}`}</Typography>
                                            ))}
                                            <br />
                                        </React.Fragment>
                                    ))}
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    </Grid>
                )}

                <Box className={classes.buttonContainer}>
                    <Button className={classes.reUploadButton} onClick={handleReupload}>
                        &lt; Re-upload File
                    </Button>
                    <Box style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            className={classes.uploadButtonStyle}
                            onClick={handleSubmitValidRecords}
                            disabled={isSubmitting || totalValidCount === 0}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Records'}
                            <ChevronRightIcon />
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Container>
                        {error && displayError}
                        {/* Show hard upload errors below the upload button */}
                            {uploadErrors.length > 0 && (
                                <Box mt={2} style={{ width: '100%' }}>
                                    {uploadErrors.map((err, idx) => (
                                        <Container
                                            key={idx}
                                            style={{
                                                width: "100%",
                                                border: "2px solid red",
                                                backgroundColor: "#ffeaea",
                                                padding: "0.8rem",
                                                marginBottom: "0.5em",
                                                textAlign: "center",
                                                fontSize: "0.875rem",
                                            }}
                                        >
                                            <Typography variant="body2">{err}</Typography>
                                        </Container>
                                    ))}
                                </Box>
                            )}
                        <Box
                            className={classes.uploadBox}
                            onClick={handleUploadBoxClick}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            style={{
                                cursor: (!file || uploadErrors.length > 0 || error) ? 'pointer' : 'not-allowed',
                                opacity: file && !(uploadErrors.length > 0 || error) ? 0.6 : 1
                            }}
                        >
                            <input
                                type="file"
                                hidden
                                ref={fileInputRef}
                                onChange={handleFileChange}
                                accept={getAcceptAttribute(fileType)}
                            />
                            <Typography className={classes.uploadText}>
                                Drag & drop file or click here to upload
                            </Typography>
                            <PublishIcon className={classes.uploadIcon} />
                        </Box>

                        <Box className={classes.file}>
                            <Typography>Select file type:</Typography>
                            <FormControl variant="outlined" size="small">
                                <Select
                                    value={fileType}
                                    onChange={(e) => setFileType(e.target.value)}
                                    disabled={file !== null && !(uploadErrors.length > 0 || error)}
                                >
                                    <MenuItem value="xlsx">.xlsx</MenuItem>
                                </Select>
                            </FormControl>

                            {file && (
                                <Box className={classes.linkBox}>
                                    <Typography className={classes.link}>
                                        {file.name}
                                    </Typography>
                                    <span onClick={handleRemoveFile} style={{ cursor: "pointer" }}>
                                        ❌
                                    </span>
                                </Box>
                            )}

                            <Box>
                                <Typography className={classes.bold}>
                                    Validation criteria
                                </Typography>
                                <Typography className={classes.criteria}>
                                    Only excel files accepted (.xls, or .xlsx)
                                </Typography>
                                <Typography>First row must contain headers only</Typography>
                                <Typography>
                                    File should not contain blank rows or columns
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                className={classes.uploadBtn}
                                endIcon={<ChevronRightIcon />}
                                onClick={handleUpload}
                                disabled={!file || isUploading || hasUploadedSuccessfully}
                            >
                                {isUploading ? 'Uploading...' : 'Upload'}
                            </Button>


                        </Box>
                    </Container>
                );
            case 1:
                return submitResults ? renderSubmitResults() : renderValidationSummary();
            default:
                return <Typography>Unknown Step</Typography>;
        }
    };

    return (
        <Box className={classes.root}>
            <Box className={classes.stepperHeader}>
                <Typography className={classes.heading}>
                    Upload Product Details
                </Typography>
                <Typography className={classes.subheading}>
                    Add / Update the list of products through the file
                </Typography>
                <Box className={classes.closePageAction} onClick={handleGoBack}>
                    <Typography style={{ fontWeight: 'bold', fontSize: "18px" }}>Cancel</Typography>
                    <CloseIcon />
                </Box>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {getStepContent(activeStep)}

            {/* Success Dialog */}
            <UploadDetailsSavedDialog
                open={uploadSuccess}
                onClose={handleCloseDialog}
                added={saveOutcome.added}
                updated={saveOutcome.updated}
            />


        </Box>
    );
};

export default UploadProductDetails;
